import React, { useEffect, useState, useRef, MouseEvent } from 'react';
import { useEvent } from 'react-use';

import { shrinkProductImage } from '@repo/utils';

interface Props {
  alt: string;
  fetchPriority?: 'high' | 'low' | 'auto';
  imageHeight?: number;
  src: string;
}

export default function ImageZoomOnHover({
  alt,
  fetchPriority = 'auto',
  imageHeight = 400,
  src,
}: Readonly<Props>) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');
  const [figureDimensions, setFigureDimensions] = useState<{
    height: string | number;
    width: string | number;
  }>({
    height: `${imageHeight}px`,
    width: 'auto',
  });

  const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
    if (typeof window !== 'undefined') {
      const { left, top, width, height } = (
        e.target as HTMLElement
      ).getBoundingClientRect();
      const x = ((e.pageX - left) / width) * 100;
      const y = ((e.pageY - window.scrollY - top) / height) * 100;

      setBackgroundPosition(`${x}% ${y}%`);
    }
  };

  const resizeFigure = () => {
    setBackgroundPosition('0% 0%');
    if (imgRef.current) {
      // Use the images intrinsic aspect ratio to hard set the width
      const img = new Image();
      img.onload = () => {
        setFigureDimensions(fdm => ({
          ...fdm,
          width: `${Number(fdm.height) * (img.width / img.height)}px`,
        }));
      };
      img.src = src;

      // But use the height of the containing box to set the height
      const { height } = imgRef.current.getBoundingClientRect();

      setFigureDimensions(fdm => ({
        ...fdm,
        height: `${height === 0 ? imageHeight : height}px`,
      }));
    }
  };

  useEvent('resize', resizeFigure);
  useEffect(resizeFigure, [imageHeight, src]);

  return (
    <figure
      role="presentation"
      className="[&>img]:hover:opacity-0 bg-no-repeat static h-full"
      onMouseMove={handleMouseMove}
      style={{
        backgroundImage: `url(${src})`,
        backgroundPosition,
        ...figureDimensions,
      }}
    >
      <img
        className="w-auto pointer-events-none"
        ref={imgRef}
        src={shrinkProductImage(src, 600)}
        alt={alt}
        fetchPriority={fetchPriority}
        style={{
          maxHeight: `${imageHeight}px`,
        }}
      />
    </figure>
  );
}
